import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const ERIC_PHOTO = "https://customer-assets.emergentagent.com/job_3d9cf4ee-16b6-4dd1-aa5d-11b4c5ff8dd4/artifacts/yoqwutsk_Eric%20Paris.webp";

export const About = () => {
    return (
        <div className="min-h-screen bg-nature-50 pt-24">
            <SEO 
                title="About Eric Paris | Certified Hypnotherapist & NLP Practitioner"
                description="Meet Eric Paris, certified hypnotherapist and NLP Practitioner based in Portugal. Creator of the Hypnosis Mastery Series — 45 professional hypnosis recordings for lasting change."
                canonical="https://hypnosismasteryseries.com/about"
            />
            
            {/* Person Schema.org Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Person",
                    "name": "Eric Paris",
                    "jobTitle": "Certified Hypnotherapist & NLP Practitioner",
                    "description": "Certified hypnotherapist and NLP Practitioner based in Portugal. Creator of the Hypnosis Mastery Series.",
                    "image": "https://customer-assets.emergentagent.com/job_3d9cf4ee-16b6-4dd1-aa5d-11b4c5ff8dd4/artifacts/yoqwutsk_Eric%20Paris.webp",
                    "url": "https://hypnosismasteryseries.com/about",
                    "knowsLanguage": ["English", "Dutch", "Portuguese"],
                    "worksFor": {
                        "@type": "Organization",
                        "name": "Hypnosis Mastery Series",
                        "url": "https://hypnosismasteryseries.com"
                    },
                    "sameAs": [
                        "https://www.youtube.com/@hypnosismasteryseries",
                        "https://www.instagram.com/hypnosismasteryseries"
                    ]
                })}
            </script>
            
            {/* Hero */}
            <section className="max-w-7xl mx-auto px-6 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="opacity-0 animate-fade-in-up">
                        <span className="inline-block px-4 py-2 bg-copper-500/10 text-copper-600 rounded-full text-sm font-body font-medium mb-6">
                            About the Creator
                        </span>
                        <h1 className="font-heading text-4xl sm:text-5xl font-light text-nature-900 leading-tight mb-6">
                            Meet <span className="italic">Eric Paris</span>
                        </h1>
                        <div className="space-y-4 text-nature-800/80 font-body leading-relaxed">
                            <p>
                                I'm Eric Paris. I'm a certified hypnotherapist, and the creator of the Hypnosis Mastery Series — a collection of guided hypnosis recordings designed to help people create lasting change from the inside out.
                            </p>
                            <p>
                                My work is for people who want a calm, practical, and respectful way to explore change without pressure or hype. Whether someone is dealing with stress, insomnia, anxiety, or a habit they're ready to leave behind, I aim to make the process feel clear, grounded, and easy to return to.
                            </p>
                            <p>
                                If you're here to understand whether this approach is right for you, I invite you to take your time and explore. The best place to begin is often the one that feels most relevant to what you want to change.
                            </p>
                        </div>
                    </div>
                    
                    <div className="opacity-0 animate-fade-in-up stagger-1">
                        <div className="relative">
                            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-hover">
                                <img 
                                    src={ERIC_PHOTO} 
                                    alt="Eric Paris - Hypnotherapist"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-soft">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-copper-500/10 flex items-center justify-center">
                                        <Headphones className="w-6 h-6 text-copper-500" />
                                    </div>
                                    <div>
                                        <p className="font-heading font-semibold text-nature-900">45</p>
                                        <p className="text-sm text-nature-800/60 font-body">Sessions</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Background and Training */}
            <section className="py-20 bg-white">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-8">
                        Background and <span className="italic">Training</span>
                    </h2>
                    
                    <div className="space-y-6 text-nature-800/80 font-body leading-relaxed">
                        <p>
                            I have spent years studying what helps people change, not just in theory, but in practice — in real sessions with real people. My training includes deep study of hypnosis, communication, and the mechanics of change, along with ongoing work to refine how I use those tools.
                        </p>
                        <p>
                            I have also drawn on the methods of respected teachers and practitioners whose work has shaped modern hypnotherapy, while developing my own style: calm, respectful, and focused on helping the unconscious mind respond in a more useful way.
                        </p>
                        <p>
                            I'm especially interested in how suggestion, language, repetition, and expectation influence the way people experience themselves. That's why my recordings are designed to be simple, steady, and easy to follow.
                        </p>
                    </div>
                </div>
            </section>

            {/* Why I Created */}
            <section className="py-20 bg-nature-50">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-8">
                        Why I Created the <span className="italic">Hypnosis Mastery Series</span>
                    </h2>
                    
                    <div className="space-y-6 text-nature-800/80 font-body leading-relaxed">
                        <p>
                            The idea for the Hypnosis Mastery Series came from a pattern I kept seeing: many people want help, but they don't always want a long explanation, a big process, or a complicated system. They want something clear, effective, and easy to return to when they need it.
                        </p>
                        <p>
                            I created this series to make guided hypnosis more accessible — not only to people who want support in the moment, but also to those who want a resource they can come back to again and again. Each recording is designed to be practical, focused, and supportive.
                        </p>
                        <p>
                            The result is a library of recordings that covers a wide range of everyday challenges, from stress and sleep to confidence, habits, and emotional balance. My goal is simple: to help as many people as possible experience change in a way that feels calm, useful, and lasting.
                        </p>
                    </div>
                </div>
            </section>

            {/* My Approach */}
            <section className="py-20 bg-nature-800">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-50 mb-8">
                        My <span className="italic">Approach</span>
                    </h2>
                    
                    <div className="space-y-6 text-nature-100/80 font-body leading-relaxed">
                        <p>
                            My approach is grounded, calm, and practical. I believe hypnosis works best when it feels like an invitation, not an instruction — a process that helps the unconscious mind become more open to useful change without pressure or struggle.
                        </p>
                        <p>
                            Rather than trying to force change, I focus on creating the conditions where change can happen more naturally. That means using clear language, steady repetition, and recordings that are simple enough to return to again and again.
                        </p>
                        <p>
                            I want the experience to feel respectful, focused, and easy to trust. No drama, no exaggerated promises, and no unnecessary complexity — just a clear process that gives your mind space to respond in its own way.
                        </p>
                        <p>
                            For me, the goal is not to overwhelm the mind, but to guide it gently toward a better direction.
                        </p>
                    </div>
                </div>
            </section>

            {/* Who I Work With */}
            <section className="py-20 bg-white">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-8">
                        Who I <span className="italic">Work With</span>
                    </h2>
                    
                    <div className="space-y-6 text-nature-800/80 font-body leading-relaxed">
                        <p>
                            The Hypnosis Mastery Series is for people who are open to working with their mind in a different way — not through force, but through calm repetition, guided focus, and a process that feels steady and manageable.
                        </p>
                        <p>
                            The recordings are especially helpful for people dealing with:
                        </p>
                        
                        <ul className="space-y-3 my-8">
                            {[
                                'Persistent anxiety or overthinking',
                                'Poor sleep or insomnia',
                                'Habits they want to shift or stop',
                                'Low confidence or self-doubt',
                                'Stress, tension, or emotional overload',
                                "A general sense that something needs to change, but they're not sure where to begin"
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="w-2 h-2 rounded-full bg-copper-500 mt-2 flex-shrink-0"></span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        
                        <p>
                            You do not need to be an expert in hypnosis to begin. You do not need to "believe hard enough" for it to work. You only need a willingness to listen, relax, and give the process a little time.
                        </p>
                        <p>
                            If that sounds like you, you are in the right place.
                        </p>
                    </div>
                </div>
            </section>

            {/* Start Here CTA */}
            <section className="py-24 bg-nature-50">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-6">
                        Start <span className="italic">Here</span>
                    </h2>
                    
                    <div className="space-y-6 text-nature-800/80 font-body leading-relaxed mb-10">
                        <p>
                            If something in what I have written resonates with you, I would be glad to have you explore the full series. The recordings are yours to keep, to return to, and to use as often as you need.
                        </p>
                        <p>
                            Lasting change is not about force. It rarely arrives in a single moment of breakthrough. It comes from consistency, from repetition, and from finally working with the part of you that has been running the patterns all along.
                        </p>
                        <p>
                            If you're not sure where to begin, try the <Link to="/recording/freedom-from-insomnia" className="text-copper-600 hover:text-copper-700 underline">free Freedom from Insomnia session</Link> — it's a good way to experience the style and pace before exploring further.
                        </p>
                        <p className="text-nature-900 font-medium">
                            I hope the Hypnosis Mastery Series can be a part of that for you.
                        </p>
                    </div>
                    
                    <Link to="/recordings" data-testid="about-cta-btn">
                        <Button 
                            size="lg" 
                            className="rounded-full bg-copper-500 hover:bg-copper-600 text-white px-10"
                        >
                            Explore All 45 Sessions
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </Link>
                </div>
            </section>

            {/* How the Sessions Are Made */}
            <section className="py-20 bg-white" data-testid="about-voice-note">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-8">
                        A note on how the sessions are <span className="italic">made</span>
                    </h2>
                    <div className="space-y-6 text-nature-800/80 font-body leading-relaxed">
                        <p>
                            Every word you hear is mine — written, structured and paced by me, drawing on the same methods I use in one-to-one work. The delivery uses a synthesised model of my own voice.
                        </p>
                        <p>
                            I mention this because I would rather you knew. It means the tone is identical across all forty-five sessions, which matters more than it might sound: consistency is part of what lets a recording work by repetition. And it means I can make a session for one person without asking them to accept a different voice from the one they have been listening to.
                        </p>
                        <p>
                            What changes between sessions is not the voice. It is the framing and the suggestions — which is where the work actually lives.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
